import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatKobo } from "../lib/format";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, lineTotal } = useCart();
  const navigate = useNavigate();

  return (
    <>
      <div
        className={`fixed inset-0 z-50 bg-sapore-ink/50 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-md bg-sapore-creamSurface shadow-2xl border-l border-sapore-border flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-sapore-border bg-sapore-purple text-sapore-cream">
          <h3 className="font-serif text-xl">Your Order</h3>
          <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="p-1 hover:text-sapore-gold">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 no-scrollbar">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-sapore-muted py-20">
              <ShoppingBag size={40} className="mb-4 text-sapore-purple/40" />
              <p className="font-medium text-sapore-inkSoft">Your cart is empty</p>
              <p className="text-sm mt-1">Add something delicious from the menu.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((p) => (
                <li key={p.key} data-testid={`cart-line-${p.key}`} className="flex gap-3 bg-white rounded-xl p-3 border border-sapore-border">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-sapore-goldSubtle flex-shrink-0">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-sapore-gold text-[10px] font-bold">SAPORE</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-sapore-ink leading-snug">{p.name}</p>
                    {p.variant && <p className="text-xs text-sapore-muted">{p.variant}</p>}
                    {p.addons?.length > 0 && (
                      <p className="text-xs text-sapore-muted">+ {p.addons.map((a) => a.name).join(", ")}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`cart-decrease-${p.key}`}
                          onClick={() => updateQty(p.key, p.quantity - 1)}
                          className="w-7 h-7 rounded-full border border-sapore-border flex items-center justify-center hover:bg-sapore-goldSubtle"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-6 text-center text-sm font-semibold">{p.quantity}</span>
                        <button
                          data-testid={`cart-increase-${p.key}`}
                          onClick={() => updateQty(p.key, p.quantity + 1)}
                          className="w-7 h-7 rounded-full border border-sapore-border flex items-center justify-center hover:bg-sapore-goldSubtle"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="font-bold text-sapore-gold text-sm">{formatKobo(lineTotal(p))}</span>
                    </div>
                  </div>
                  <button
                    data-testid={`cart-remove-${p.key}`}
                    onClick={() => removeItem(p.key)}
                    className="text-sapore-muted hover:text-red-600 self-start"
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-sapore-border px-6 py-5 bg-white">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sapore-inkSoft">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-bold text-lg text-sapore-ink">{formatKobo(subtotal)}</span>
            </div>
            <p className="text-xs text-sapore-muted mb-4">Delivery fee calculated at checkout by zone.</p>
            <button
              data-testid="cart-checkout-button"
              onClick={() => {
                setOpen(false);
                navigate("/checkout");
              }}
              className="w-full py-3.5 rounded-full bg-sapore-gold text-sapore-ink font-semibold hover:bg-sapore-goldHover transition-colors hover:scale-[1.01] active:scale-[0.99]"
            >
              Proceed to Checkout
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
